// Story 15.6 — the Screen 24 edit write (AC9, AC25).
//
// Action-in-path (D18): `POST /admin/orgs/{id}/update-details`. Never
// `PUT /admin/orgs/{id}` — the repo has zero PUT/PATCH/DELETE across 77 modules.
//
// INVALIDATE-AND-REFETCH, BOTH KEYS. The 200 body is deliberately partial — it
// carries no email address and no billing address — so it cannot be written into
// the detail cache without blanking the fields it omits. Instead:
//   * the detail key, because the screen the edit fired from must restate;
//   * the ["admin-orgs"] PREFIX, because a renamed org (or a newly set CRM key)
//     changes a directory ROW, and a partial key match refetches every cached
//     filter permutation rather than only the one last viewed.
//
// Refusals surface as a typed `ApiError` for the screen's ONE mapper to branch
// on by `code` (409 ORG-016 duplicate_external_reference, 409 ORG-018
// external_reference_already_set, an un-coded 404, a 403) — never on the
// server's English `msg`.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { ADMIN_ORGS_QUERY_KEY } from "@/hooks/queries/useAdminOrgs";
import { adminOrgDetailQueryKey } from "@/hooks/queries/useAdminOrgDetail";
import type {
  UpdateOrganizationData,
  UpdateOrganizationRequest,
} from "@/hooks/queries/admin-orgs-types";

export function updateOrganizationPath(organizationId: string): string {
  return `/admin/orgs/${encodeURIComponent(organizationId)}/update-details`;
}

export function useUpdateOrganization(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateOrganizationRequest) =>
      apiFetch<UpdateOrganizationData>(updateOrganizationPath(organizationId), {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOrgDetailQueryKey(organizationId) });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORGS_QUERY_KEY] });
    },
  });
}
